import { useEffect, useState } from "react";
import { PieChart } from "react-minimal-pie-chart";

import { MetricBand } from "~/components/visibilityPerformance/bands";
import { useTheme } from "~/contexts/ThemeContext";

import styles from "./ScoreDonut.module.scss";

interface Props {
  /** The score to render, 0-100. */
  value: number;
  band: MetricBand;
  /** Diameter in px. */
  size?: number;
  /** Ring thickness as a % of radius (react-minimal-pie-chart). */
  lineWidth?: number;
  /** Unit shown after the number. Defaults to "%". */
  suffix?: string;
  /** Override the centred number's font size (defaults to scale with size). */
  fontSize?: string;
  /** Optional details tooltip shown on hover. */
  tooltip?: string;
}

const TEXT_CLASS: Record<MetricBand, string> = {
  good: styles.good,
  warn: styles.warn,
  bad: styles.bad,
};

// Compact donut dimensions shared by the standalone table cell and the donut
// inside GainSparkline, so both render at the same height.
export const COMPACT_DONUT_PROPS = {
  size: 28,
  lineWidth: 12,
  fontSize: "12px",
} as const;

// react-minimal-pie-chart sets `color` as a fill *attribute*, which can't read
// CSS vars — so resolve the token values from computed styles (and re-read on
// theme change) to stay correct in dark mode.
function readBandColors(): Record<MetricBand, string> & { track: string } {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) =>
    s.getPropertyValue(name).trim() || fallback;

  return {
    good: v("--service-green3", "#0e9858"),
    warn: v("--service-orange3", "#e65100"),
    bad: v("--service-red3", "#cd4444"),
    track: v("--functional-grey3", "#e6e6e6"),
  };
}

const ScoreDonut = ({
  value,
  band,
  size = 46,
  lineWidth = 12,
  suffix = "%",
  fontSize,
  tooltip,
}: Props) => {
  const { theme } = useTheme();
  const [colors, setColors] = useState(readBandColors);

  useEffect(() => {
    setColors(readBandColors());
  }, [theme]);

  return (
    <span
      className={styles.wrap}
      style={{ width: size, height: size, ...(tooltip && { cursor: "help" }) }}
      data-tooltip-id={tooltip ? "vm-header-tooltip" : undefined}
      data-tooltip-content={tooltip}
    >
      <PieChart
        data={[
          { value, color: colors[band] },
          { value: 100 - value, color: colors.track },
        ]}
        lineWidth={lineWidth}
        startAngle={270}
        animate={false}
      />
      <span
        className={`${styles.center} ${TEXT_CLASS[band]}`}
        style={{ fontSize: fontSize ?? `${size * 0.32}px` }}
      >
        {value}
        {suffix}
      </span>
    </span>
  );
};

export default ScoreDonut;
